import { http, unwrap } from '@/api/client'
import type { ApiEnvelope } from '@/types/api'
import { i18n } from './index'
import { DEFAULT_LOCALE, isSupportedLocale, type SupportedLocale } from './locales'

const STORAGE_KEY = 'elfie-locale'

/** `{ 'users.roles.admin': 'x' }` → `{ users: { roles: { admin: 'x' } } }`, the shape vue-i18n wants. */
export const nestMessages = (flat: Record<string, string>): Record<string, unknown> => {
  const root: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split('.')
    let node = root
    for (const part of parts.slice(0, -1)) {
      const next = node[part]
      if (typeof next !== 'object' || next === null) node[part] = {}
      node = node[part] as Record<string, unknown>
    }
    const leaf = parts[parts.length - 1] as string
    // A key that is both a leaf and a branch cannot be represented; the branch wins.
    if (typeof node[leaf] !== 'object') node[leaf] = value
  }
  return root
}

/** The pre-login choice. The user's saved preference, once known, overrides it. */
export const readStoredLocale = (): SupportedLocale | null => {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return isSupportedLocale(value) ? value : null
  } catch {
    return null
  }
}

const storeLocale = (locale: SupportedLocale): void => {
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // Private browsing. The choice still holds for this page load.
  }
}

const loaded = new Set<string>([DEFAULT_LOCALE])

/*
 * The instance is typed by the English file, so it only knows 'en-US' and the
 * exact English tree. Loaded locales are looser than that by design.
 */
const runtime = i18n.global as unknown as {
  locale: { value: string }
  setLocaleMessage: (locale: string, messages: Record<string, unknown>) => void
}

/**
 * Switches the UI language. Fetches the locale from the API the first time it
 * is asked for; en-US never fetches. Safe to call with a locale already shown.
 */
export const applyLocale = async (locale: SupportedLocale): Promise<void> => {
  if (!loaded.has(locale)) {
    const { data } = await http.get<ApiEnvelope<{ locale: string; messages: Record<string, string> }>>(
      `/i18n/messages/${locale}`,
    )
    runtime.setLocaleMessage(locale, nestMessages(unwrap(data).messages))
    loaded.add(locale)
  }
  runtime.locale.value = locale
  document.documentElement.lang = locale
  storeLocale(locale)
}

/** Drops a fetched locale so the next `applyLocale` refetches — used after an admin edit. */
export const invalidateLocale = (locale: SupportedLocale): void => {
  if (locale !== DEFAULT_LOCALE) loaded.delete(locale)
}

export const currentLocale = (): SupportedLocale => {
  const value = runtime.locale.value
  return isSupportedLocale(value) ? value : DEFAULT_LOCALE
}
